import { defineStore } from 'pinia';
import { dealService } from '@/services/dealService';
import { projectService } from '@/services/projectService';

export const useDealStore = defineStore('deals', {
  state: () => ({
    // Pipelines
    pipelines: [],
    pipelinesLoading: false,
    currentPipelineId: null,

    // Kanban data
    kanbanData: null,
    kanbanLoading: false,

    // Single deal
    currentDeal: null,
    currentDealLoading: false,

    // Deal lists
    deals: [],
    dealsLoading: false,
    dealsPagination: {
      total: 0,
      page: 1,
      limit: 50,
      total_pages: 0,
    },

    // Statistics
    stats: null,
    statsLoading: false,

    // Export
    exporting: false,

    // Selection for bulk operations
    selectedDealIds: [],

    // Filters
    filters: {
      owner_id: null,
      tag_ids: [],
      search: '',
      status: 'active',
    },

    // UI state
    showDealDrawer: false,
    selectedDealId: null,

    // Deal nested resources
    dealProposals: [],
    dealProposalsLoading: false,
    dealSubmissions: [],
    dealSubmissionsLoading: false,
    dealActivities: [],
    dealActivitiesLoading: false,
    dealFormLinks: [],
    dealFormLinksLoading: false,
    dealTasks: [],
    dealTasksLoading: false,

    // Error handling
    error: null,
  }),

  getters: {
    /**
     * Get the current pipeline object
     */
    currentPipeline: (state) => {
      return state.pipelines.find((p) => p.id === state.currentPipelineId);
    },

    /**
     * Get the default pipeline
     */
    defaultPipeline: (state) => {
      return state.pipelines.find((p) => p.is_default);
    },

    /**
     * Check if there are selected deals
     */
    hasSelection: (state) => {
      return state.selectedDealIds.length > 0;
    },

    /**
     * Get number of selected deals
     */
    selectionCount: (state) => {
      return state.selectedDealIds.length;
    },

    /**
     * Get kanban stages with deals
     */
    kanbanStages: (state) => {
      return state.kanbanData?.stages || [];
    },

    /**
     * Archived deals for the current pipeline, shown as their own column
     */
    kanbanArchive: (state) => {
      return state.kanbanData?.archive || { deals: [], deals_count: 0, total_value: 0 };
    },

    /**
     * Get kanban totals
     */
    kanbanTotals: (state) => {
      return state.kanbanData?.totals || {
        total_deals: 0,
        total_value: 0,
        weighted_value: 0,
      };
    },

    /**
     * Check if current deal is won
     */
    isCurrentDealWon: (state) => {
      return !!state.currentDeal?.won_at;
    },

    /**
     * Check if current deal is lost
     */
    isCurrentDealLost: (state) => {
      return !!state.currentDeal?.lost_at;
    },

    /**
     * Check if current deal is closed (won or lost)
     */
    isCurrentDealClosed: (state) => {
      return !!state.currentDeal?.won_at || !!state.currentDeal?.lost_at;
    },
  },

  actions: {
    // ============================================
    // PIPELINES
    // ============================================

    /**
     * Fetch all pipelines
     */
    async fetchPipelines() {
      this.pipelinesLoading = true;
      this.error = null;

      try {
        const { pipelines } = await dealService.getPipelines();
        this.pipelines = pipelines;

        // Set default pipeline if none selected
        if (!this.currentPipelineId && pipelines.length > 0) {
          const defaultPipeline = pipelines.find((p) => p.is_default);
          this.currentPipelineId = defaultPipeline?.id || pipelines[0]?.id;
        }

        return pipelines;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.pipelinesLoading = false;
      }
    },

    /**
     * Set the current pipeline
     */
    setCurrentPipeline(pipelineId) {
      this.currentPipelineId = pipelineId;
      // Clear kanban data when switching pipelines
      this.kanbanData = null;
    },

    /**
     * Create a new pipeline
     */
    async createPipeline(pipelineData) {
      try {
        const pipeline = await dealService.createPipeline(pipelineData);
        this.pipelines.push(pipeline);
        return pipeline;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Update a pipeline
     */
    async updatePipeline(pipelineId, pipelineData) {
      try {
        const pipeline = await dealService.updatePipeline(pipelineId, pipelineData);
        const index = this.pipelines.findIndex((p) => p.id === pipelineId);
        if (index !== -1) {
          this.pipelines[index] = pipeline;
        }
        return pipeline;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Delete a pipeline
     */
    async deletePipeline(pipelineId) {
      try {
        await dealService.deletePipeline(pipelineId);
        this.pipelines = this.pipelines.filter((p) => p.id !== pipelineId);

        // If deleted pipeline was current, switch to default
        if (this.currentPipelineId === pipelineId) {
          const defaultPipeline = this.pipelines.find((p) => p.is_default);
          this.currentPipelineId = defaultPipeline?.id || this.pipelines[0]?.id;
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    // ============================================
    // KANBAN
    // ============================================

    /**
     * Fetch kanban data for current pipeline
     */
    async fetchKanban({ silent = false } = {}) {
      if (!this.currentPipelineId) {
        console.warn('No pipeline selected, cannot fetch kanban');
        return;
      }

      if (!silent) this.kanbanLoading = true;
      this.error = null;

      try {
        const params = {
          owner_id: this.filters.owner_id || undefined,
          search: this.filters.search || undefined,
        };
        // Send tag_ids as a comma-separated list (only when filtering)
        if (Array.isArray(this.filters.tag_ids) && this.filters.tag_ids.length) {
          params.tag_ids = this.filters.tag_ids.join(',');
        }
        const data = await dealService.getKanban(this.currentPipelineId, params);
        this.kanbanData = data;
        return data;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        if (!silent) this.kanbanLoading = false;
      }
    },

    /**
     * Move deal to a different stage (with optimistic update)
     */
    async moveDealToStage(dealId, newStageId, oldStageId, fromArchive = false) {
      // A restore already landed in the target column via drag. Refetch after
      // the API call so stage counts stay correct.
      if (!fromArchive && this.kanbanData) {
        const oldStage = this.kanbanData.stages.find((s) => s.id === oldStageId);
        const newStage = this.kanbanData.stages.find((s) => s.id === newStageId);

        if (oldStage && newStage) {
          const dealIndex = oldStage.deals.findIndex((d) => d.id === dealId);
          if (dealIndex !== -1) {
            const [deal] = oldStage.deals.splice(dealIndex, 1);
            deal.stage_id = newStageId;
            newStage.deals.push(deal);

            // Update counts
            oldStage.deals_count--;
            oldStage.total_value -= deal.value || 0;
            newStage.deals_count++;
            newStage.total_value += deal.value || 0;
          }
        }
      }

      try {
        await dealService.moveToStage(dealId, newStageId);
        if (fromArchive) await this.fetchKanban({ silent: true });
      } catch (error) {
        // Revert optimistic update on error
        await this.fetchKanban({ silent: true });
        this.error = error.message;
        throw error;
      }
    },

    // ============================================
    // DEALS
    // ============================================

    /**
     * Fetch deals with filters
     */
    async fetchDeals(params = {}) {
      this.dealsLoading = true;
      this.error = null;

      try {
        const { deals, pagination } = await dealService.getDeals({
          ...this.filters,
          ...params,
        });
        this.deals = deals;
        this.dealsPagination = pagination;
        return { deals, pagination };
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.dealsLoading = false;
      }
    },

    /**
     * Fetch a single deal
     */
    async fetchDeal(dealId) {
      this.currentDealLoading = true;
      this.error = null;

      try {
        const deal = await dealService.getDealById(dealId);
        this.currentDeal = deal;
        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.currentDealLoading = false;
      }
    },

    /**
     * Create a new deal
     */
    async createDeal(dealData) {
      try {
        const deal = await dealService.createDeal(dealData);

        // Add to kanban if same pipeline
        if (this.kanbanData && deal.pipeline_id === this.currentPipelineId) {
          const stage = this.kanbanData.stages.find((s) => s.id === deal.stage_id);
          if (stage) {
            stage.deals.unshift(deal);
            stage.deals_count++;
            stage.total_value += deal.value || 0;
          }
        }

        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Update a deal
     */
    async updateDeal(dealId, dealData) {
      try {
        const deal = await dealService.updateDeal(dealId, dealData);

        // Update current deal if it's the same
        if (this.currentDeal?.id === dealId) {
          this.currentDeal = deal;
        }

        // Update in kanban
        if (this.kanbanData) {
          for (const stage of this.kanbanData.stages) {
            const index = stage.deals.findIndex((d) => d.id === dealId);
            if (index !== -1) {
              stage.deals[index] = { ...stage.deals[index], ...deal };
              break;
            }
          }
        }

        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Delete a deal
     */
    async deleteDeal(dealId) {
      try {
        await dealService.deleteDeal(dealId);

        // Remove from kanban
        if (this.kanbanData) {
          for (const stage of this.kanbanData.stages) {
            const index = stage.deals.findIndex((d) => d.id === dealId);
            if (index !== -1) {
              const deal = stage.deals[index];
              stage.deals.splice(index, 1);
              stage.deals_count--;
              stage.total_value -= deal.value || 0;
              break;
            }
          }
        }

        // Clear current deal if it was deleted
        if (this.currentDeal?.id === dealId) {
          this.currentDeal = null;
          this.closeDealDrawer();
        }
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Mark deal as won
     */
    async markDealAsWon(dealId) {
      try {
        const deal = await dealService.markAsWon(dealId);

        if (this.currentDeal?.id === dealId) {
          this.currentDeal = deal;
        }

        // Refresh kanban to show updated stage
        await this.fetchKanban();

        // Refresh project status (backend sets draft -> active)
        if (this.currentDeal?.id === dealId) {
          await this.fetchDeal(dealId);
          try {
            const project = await projectService.getProjectByDealId(dealId);
            if (project && project.status === 'active') {
              this.currentDeal = { ...this.currentDeal, project };
            }
          } catch (e) {
            // Non-critical, project refresh failed
          }
        }

        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Mark deal as lost
     */
    async markDealAsLost(dealId, reason = null) {
      try {
        const deal = await dealService.markAsLost(dealId, reason);

        if (this.currentDeal?.id === dealId) {
          this.currentDeal = deal;
        }

        // Refresh kanban to show updated stage
        await this.fetchKanban();

        // Backend side-effect: draft projects -> cancelled, open work orders -> cancelled
        try {
          const project = await projectService.getProjectByDealId(dealId);
          if (project && this.currentDeal?.id === dealId) {
            this.currentDeal = { ...this.currentDeal, project };
          }
        } catch (e) {
          // Non-critical, project refresh failed
        }

        if (this.currentDeal?.id === dealId) {
          await this.fetchDeal(dealId);
        }

        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Reopen a closed deal
     */
    async reopenDeal(dealId) {
      try {
        const deal = await dealService.reopenDeal(dealId);

        if (this.currentDeal?.id === dealId) {
          this.currentDeal = deal;
        }

        await this.fetchKanban();

        // Backend side-effect: cancelled (was draft) projects -> draft again
        try {
          const project = await projectService.getProjectByDealId(dealId);
          if (project && this.currentDeal?.id === dealId) {
            this.currentDeal = { ...this.currentDeal, project };
          }
        } catch (e) {
          // Non-critical, project refresh failed
        }

        if (this.currentDeal?.id === dealId) {
          await this.fetchDeal(dealId);
        }

        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    // ============================================
    // EXPORT
    // ============================================

    /**
     * Export deals as an Excel file using the current pipeline + filters.
     * Sheet "Deals" is one row per deal; sheet "Productregels" is one row per
     * quote line. Triggers a browser download of the returned file.
     * @param {Object} overrides - optional extra params (e.g. { stage_ids, status })
     */
    async exportDeals(overrides = {}) {
      this.exporting = true;
      this.error = null;

      try {
        const params = {};

        if (this.currentPipelineId) {
          params.pipeline_id = this.currentPipelineId;
        }
        if (this.filters.owner_id) {
          params.owner_id = this.filters.owner_id;
        }
        if (Array.isArray(this.filters.tag_ids) && this.filters.tag_ids.length) {
          params.tag_ids = this.filters.tag_ids.join(',');
        }
        if (this.filters.search) {
          params.search = this.filters.search;
        }
        // Default to exporting all statuses (active, won, lost, archived).
        params.status = 'all';
        params.format = 'xlsx';

        // Caller-provided params win (e.g. explicit stage selection or status).
        Object.assign(params, overrides);

        const { blob, filename } = await dealService.exportDeals(params);

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.exporting = false;
      }
    },

    // ============================================
    // STATISTICS
    // ============================================

    /**
     * Fetch deal statistics
     */
    async fetchStats(period = '30d') {
      this.statsLoading = true;

      try {
        const stats = await dealService.getStats({ period });
        this.stats = stats;
        return stats;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.statsLoading = false;
      }
    },

    // ============================================
    // SELECTION
    // ============================================

    /**
     * Toggle deal selection
     */
    toggleDealSelection(dealId) {
      const index = this.selectedDealIds.indexOf(dealId);
      if (index > -1) {
        this.selectedDealIds.splice(index, 1);
      } else {
        this.selectedDealIds.push(dealId);
      }
    },

    /**
     * Select a deal (single or multi)
     */
    selectDeal(dealId, multi = false) {
      if (multi) {
        this.toggleDealSelection(dealId);
      } else {
        this.selectedDealIds = [dealId];
      }
    },

    /**
     * Clear all selections
     */
    clearSelection() {
      this.selectedDealIds = [];
    },

    /**
     * Select all deals in kanban
     */
    selectAllDeals() {
      if (!this.kanbanData) return;

      this.selectedDealIds = this.kanbanData.stages.flatMap((stage) =>
        stage.deals.map((deal) => deal.id)
      );
    },

    // ============================================
    // BULK OPERATIONS
    // ============================================

    /**
     * Bulk move selected deals to stage
     */
    async bulkMoveToStage(stageId) {
      if (this.selectedDealIds.length === 0) return;

      try {
        await dealService.bulkMoveToStage(this.selectedDealIds, stageId);
        this.clearSelection();
        await this.fetchKanban({ silent: true });
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Bulk assign selected deals to owner
     */
    async bulkAssign(ownerId) {
      if (this.selectedDealIds.length === 0) return;

      try {
        await dealService.bulkAssign(this.selectedDealIds, ownerId);
        this.clearSelection();
        await this.fetchKanban();
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Bulk archive selected deals
     */
    async bulkArchive() {
      if (this.selectedDealIds.length === 0) return;

      try {
        await dealService.bulkArchive(this.selectedDealIds);
        this.clearSelection();
        await this.fetchKanban({ silent: true });
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Archive one deal, for example after a drop on the archive column
     */
    async archiveDeal(dealId) {
      try {
        await dealService.bulkArchive([dealId]);
        this.selectedDealIds = this.selectedDealIds.filter((id) => id !== dealId);
        await this.fetchKanban({ silent: true });
      } catch (error) {
        await this.fetchKanban({ silent: true });
        this.error = error.message;
        throw error;
      }
    },

    // ============================================
    // DEAL DRAWER
    // ============================================

    /**
     * Open deal drawer
     */
    openDealDrawer(dealId) {
      this.selectedDealId = dealId;
      this.showDealDrawer = true;
    },

    /**
     * Close deal drawer
     */
    closeDealDrawer() {
      this.showDealDrawer = false;
      this.selectedDealId = null;
      this.currentDeal = null;
      // Clear nested resources
      this.dealProposals = [];
      this.dealSubmissions = [];
      this.dealActivities = [];
      this.dealFormLinks = [];
    },

    // ============================================
    // DEAL NESTED RESOURCES
    // ============================================

    /**
     * Fetch proposals for current deal
     */
    async fetchDealProposals(dealId) {
      this.dealProposalsLoading = true;

      try {
        const { proposals } = await dealService.getDealProposals(dealId);
        this.dealProposals = proposals || [];
        return proposals;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.dealProposalsLoading = false;
      }
    },

    /**
     * Fetch submissions for current deal
     */
    async fetchDealSubmissions(dealId) {
      this.dealSubmissionsLoading = true;

      try {
        const { submissions } = await dealService.getDealSubmissions(dealId);
        this.dealSubmissions = submissions || [];
        return submissions;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.dealSubmissionsLoading = false;
      }
    },

    /**
     * Fetch activities for current deal
     */
    async fetchDealActivities(dealId) {
      this.dealActivitiesLoading = true;

      try {
        const { activities } = await dealService.getDealActivities(dealId);
        this.dealActivities = activities || [];
        return activities;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.dealActivitiesLoading = false;
      }
    },

    /**
     * Create an activity for current deal
     * API returns { activity: Activity }; we push the activity object to dealActivities
     */
    async createDealActivity(dealId, activityData) {
      try {
        const activity = await dealService.createDealActivity(dealId, activityData);
        if (activity && typeof activity.id !== 'undefined') {
          const toPush = { ...activity };
          if (toPush.assigned_user && !toPush.user) {
            toPush.user = toPush.assigned_user;
          }
          this.dealActivities.unshift(toPush);
        }
        return activity;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Fetch form links for current deal
     */
    async fetchDealFormLinks(dealId) {
      this.dealFormLinksLoading = true;

      try {
        const { links } = await dealService.getFormLinks(dealId);
        this.dealFormLinks = links || [];
        return links;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.dealFormLinksLoading = false;
      }
    },

    /**
     * Create a form link for current deal
     */
    async createDealFormLink(dealId, linkData) {
      try {
        const link = await dealService.createFormLink(dealId, linkData);
        this.dealFormLinks.unshift(link);
        return link;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Delete a form link
     */
    async deleteDealFormLink(dealId, linkId) {
      try {
        await dealService.deleteFormLink(dealId, linkId);
        this.dealFormLinks = this.dealFormLinks.filter((l) => l.id !== linkId);
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Toggle deal favorite status
     */
    async toggleFavorite(dealId, isFavorite) {
      try {
        const deal = await dealService.toggleFavorite(dealId, isFavorite);

        if (this.currentDeal?.id === dealId) {
          this.currentDeal = deal;
        }

        return deal;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Delete an activity
     */
    async deleteActivity(activityId) {
      try {
        await dealService.deleteActivity(activityId);
        this.dealActivities = this.dealActivities.filter((a) => a.id !== activityId);
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    // ============================================
    // DEAL TASKS
    // ============================================

    /**
     * Fetch tasks for current deal
     */
    async fetchDealTasks(dealId) {
      this.dealTasksLoading = true;

      try {
        const { tasks } = await dealService.getDealTasks(dealId);
        this.dealTasks = tasks || [];
        return tasks;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.dealTasksLoading = false;
      }
    },

    /**
     * Create a task for current deal
     */
    async createDealTask(dealId, taskData) {
      try {
        const task = await dealService.createDealTask(dealId, taskData);
        this.dealTasks.unshift(task);
        return task;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Update a deal task
     */
    async updateDealTask(taskId, taskData) {
      try {
        const task = await dealService.updateTask(taskId, taskData);
        const index = this.dealTasks.findIndex((t) => t.id === taskId);
        if (index !== -1) {
          this.dealTasks[index] = { ...this.dealTasks[index], ...task };
        }
        return task;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Delete a deal task
     */
    async deleteDealTask(taskId) {
      try {
        await dealService.deleteTask(taskId);
        this.dealTasks = this.dealTasks.filter((t) => t.id !== taskId);
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Mark a deal task as completed
     */
    async completeDealTask(taskId) {
      try {
        const task = await dealService.completeTask(taskId);
        const index = this.dealTasks.findIndex((t) => t.id === taskId);
        if (index !== -1) {
          this.dealTasks[index] = { ...this.dealTasks[index], ...task };
        }
        return task;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    /**
     * Reopen a completed deal task
     */
    async reopenDealTask(taskId) {
      try {
        const task = await dealService.reopenTask(taskId);
        const index = this.dealTasks.findIndex((t) => t.id === taskId);
        if (index !== -1) {
          this.dealTasks[index] = { ...this.dealTasks[index], ...task };
        }
        return task;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    // ============================================
    // FILTERS
    // ============================================

    /**
     * Set search filter
     */
    setSearchFilter(search) {
      this.filters.search = search;
    },

    /**
     * Set owner filter
     */
    setOwnerFilter(ownerId) {
      this.filters.owner_id = ownerId;
    },

    /**
     * Set tag filter (full desired set of tag ids)
     */
    setTagFilter(tagIds) {
      this.filters.tag_ids = Array.isArray(tagIds) ? [...tagIds] : [];
    },

    /**
     * Set status filter
     */
    setStatusFilter(status) {
      this.filters.status = status;
    },

    /**
     * Clear all filters
     */
    clearFilters() {
      this.filters = {
        owner_id: null,
        tag_ids: [],
        search: '',
        status: 'active',
      };
    },

    // ============================================
    // RESET
    // ============================================

    /**
     * Reset store to initial state
     */
    $reset() {
      this.pipelines = [];
      this.pipelinesLoading = false;
      this.currentPipelineId = null;
      this.kanbanData = null;
      this.kanbanLoading = false;
      this.currentDeal = null;
      this.currentDealLoading = false;
      this.deals = [];
      this.dealsLoading = false;
      this.dealsPagination = {
        total: 0,
        page: 1,
        limit: 50,
        total_pages: 0,
      };
      this.stats = null;
      this.statsLoading = false;
      this.selectedDealIds = [];
      this.filters = {
        owner_id: null,
        search: '',
        status: 'active',
      };
      this.showDealDrawer = false;
      this.selectedDealId = null;
      this.dealProposals = [];
      this.dealSubmissions = [];
      this.dealActivities = [];
      this.dealFormLinks = [];
      this.error = null;
    },
  },
});
